<template>
  <div class="featured-card" data-slot="root" :class="ui?.root">
    <div
      v-if="$slots['slide-badges']"
      class="slide-badges mb-5 max-md:mb-2.5 flex gap-1"
    >
      <slot name="slide-badges" />
    </div>

    <div class="info" :class="ui?.info" data-slot="info">
      <NuxtLink
        v-if="title"
        class="title line-clamp-4"
        :to="to"
        :class="ui?.title"
      >
        {{ title }}
      </NuxtLink>
      <p
        v-if="description"
        class="description mb-14.5 max-lg:mb-5"
        :class="ui?.description"
      >
        {{ description }}
      </p>
    </div>

    <slot name="button" />

    <div
      class="links flex flex-wrap gap-4"
      :class="ui?.links"
      data-slot="links"
    >
      <div
        v-for="(badge, index) in data?.links"
        :key="index"
        class="link-badge flex w-[calc(50%-8px)] min-w-max items-center gap-2.5"
        :class="{
          'max-w-max': index === 1,
        }"
      >
        <IconBadge
          :class="['w-8 h-8 max-md:w-6 max-md:h-6', ui?.badge].join(' ')"
          :icon="badge.icon"
          :ui="{
            icon: 'w-6 h-6 max-md:w-4 max-md:h-4 text-white',
            wrapper: `bg-accent-default ${badge.color || ''}`,
          }"
        />
        <NuxtLink v-if="badge.to" :to="badge.to">
          <span class="link-text">{{ badge.text }}</span>
        </NuxtLink>

        <span v-else class="link-text">{{ badge.text }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup generic="T = CardData">
import type { RouteLocationRaw } from 'vue-router'
import type { CardData } from '~/types'

defineOptions({
  name: 'FeaturedCard',
})

defineProps<{
  data?: CardData
  title?: string
  description?: string
  background?: string | null
  to?: RouteLocationRaw
  links?: {
    icon: string
    text: string | number
    color?: 'success' | 'secondary'
  }[]
  ui?: {
    root?: string
    info?: string
    title?: string
    description?: string
    links?: string
    badge?: string
  }
}>()
</script>

<style lang="scss" scoped>
.featured-card {
  padding: 32px;
  width: 100%;
  height: 294px;
  border-radius: var(--radius-radius-20, 20px);
  border: 1px solid var(--stroke-for-glass, rgba(255, 255, 255, 0.7));
  background:
    linear-gradient(0deg, rgba(1, 43, 90, 0.3), rgba(1, 43, 90, 0.3)),
    linear-gradient(0deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.3));

  backdrop-filter: blur(20px);

  @include max-lg {
    padding: 24px;
    display: flex;
    flex-direction: column;
  }

  @include isMobile {
    padding: 16px;
    height: 184px;
    max-height: 184px;
  }
}

.title {
  @include typo('heading-h3');
  color: var(--static-white, #fff);
  margin-bottom: 16px;
}

.description {
  overflow: hidden;
  color: var(--color-white, #fff);
  text-overflow: ellipsis;

  @include typo('paragraph-sm-regular');
}

.info {
  margin-bottom: 40px;

  @include max-lg {
    margin-bottom: 0;

    & + * + * {
      margin-top: auto;
    }
  }
}

.link-text {
  color: var(--static-white, #fff);
  font-family: var(--family-paragraph, Inter);
  font-size: var(--paragraph-font-size-SM-medium, 14px);
  font-style: normal;
  font-weight: 500;
  line-height: var(--paragraph-line-height-SM-medium, 18px); /* 128.571% */
  letter-spacing: var(--paragraph-letter-spacing-SM-medium, 0);
}
</style>
